import { gsap } from "gsap";
import ScrollTrigger from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';
gsap.registerPlugin(ScrollTrigger, SplitText);

import { horizontalLoop } from '../snippets/horizontalLoop'
import flipbutton from '../snippets/flipbutton'
import { onWidthResize } from '../utils/resize.js';
import { eagerLoadNearView } from '../utils/eager-images.js';

export default class CollectionMultiSlider {
    constructor(container) {
        this.container = container;
        this.tabs = this.container.querySelectorAll('.collection-multi-slider__tab');
        this.wrappers = this.container.querySelectorAll('[data-collection-panel]');
        this.footers = this.container.querySelectorAll('[data-collection-footer]');
        this.currentIndex = 0;
        this.loops = {};

        this.title = this.container.querySelector('.collection-multi-slider__title');
        //  this.text_lines = new SplitText(this.title, { type: 'lines,words', linesClass: "lineParent" });

        this.flipbuttons = this.container.querySelectorAll('.collection-multi-slider__footer .js-flipbutton');

        this.init();
        this.events();
    }

    init = () => {
        eagerLoadNearView(this.container);

        // Initialize horizontalLoop for each collection slider
        this.wrappers.forEach((wrapper, wrapperIndex) => {
            const panelIndex = parseInt(wrapper.dataset.collectionPanel);
            const boxes = gsap.utils.toArray(wrapper.querySelectorAll('.collection-multi-slider__item'));

            //const isActive = wrapper.classList.contains('collection-multi-slider__wrapper--active');

            this.loops[panelIndex] = horizontalLoop(boxes, { gap: 20, paused: true, reversed: false, draggable: true, center: false, repeat: -1 });

            gsap.set(boxes, { opacity: 0, yPercent: 40 });
            ScrollTrigger.batch(boxes, {
                interval: 0.2,
                batchMax: 7,
                start: "top-=40px bottom",
                toggleActions: "play pause resume reset",
                onEnter: (elements) => {
                    gsap.to(elements, {
                        opacity: 1,
                        yPercent: 0,
                        duration: 1.6,
                        stagger: { each: 0.1 },
                        overwrite: 'auto',
                        ease: 'expo.out'
                    });
                }
            });
        });

        this.flipbuttons.forEach(element => {
            new flipbutton(element);
        });

        // Size the title row to the header width (grow on 4K, shrink if overflow)
        this.fitTitle();
        onWidthResize(this.onResize);
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(this.fitTitle);
        }
    }

    // Fit the single-line tab row to the header width in both directions so
    // the title stays full-bleed from mobile through 4K. Gaps are em-based, so
    // content width scales linearly with font-size and a single pass is exact
    // (0.99 leaves a sub-pixel safety margin).
    fitTitle = () => {
        if (!this.title) return;

        this.title.style.fontSize = '';

        const available = this.title.clientWidth;
        const content = this.title.scrollWidth;

        if (available <= 0 || content <= 0) return;

        const base = parseFloat(getComputedStyle(this.title).fontSize);
        const fitted = base * (available / content) * 0.99;

        if (Math.abs(fitted - base) < 0.5) return;

        this.title.style.fontSize = `${fitted}px`;
    }

    onResize = () => {
        if (this._fitRaf) cancelAnimationFrame(this._fitRaf);
        this._fitRaf = requestAnimationFrame(this.fitTitle);
    }

    events = () => {
        // Tab switching - use data attribute to get correct index
        this.tabs.forEach((tab) => {
            tab.addEventListener('click', (e) => {
                e.preventDefault();
                const tabIndex = parseInt(tab.dataset.collectionTab);
                this.switchCollection(tabIndex);
            });
        });

        // Arrow navigation for each slider
        this.wrappers.forEach((wrapper, index) => {
            const prevBtn = wrapper.querySelector(`[data-slider-prev="${index}"]`);
            const nextBtn = wrapper.querySelector(`[data-slider-next="${index}"]`);

            if (prevBtn && this.loops[index]) {
                prevBtn.addEventListener('click', () => {
                    this.loops[index].previous({ duration: 0.4, ease: "power1.inOut" });
                });
            }

            if (nextBtn && this.loops[index]) {
                nextBtn.addEventListener('click', () => {
                    this.loops[index].next({ duration: 0.4, ease: "power1.inOut" });
                });
            }

            // Click on items to center them
            const boxes = wrapper.querySelectorAll('.collection-multi-slider__item');
            boxes.forEach((box, i) => {
                box.addEventListener('click', () => {
                    if (this.loops[index]) {
                        this.loops[index].toIndex(i, { duration: 0.8, ease: "power1.inOut" });
                    }
                });
            });
        });
    }

    switchCollection = (newIndex) => {

        // Find the actual wrapper and footer with matching panel index
        const currentWrapper = this.container.querySelector(`[data-collection-panel="${this.currentIndex}"]`);
        const currentFooter = this.container.querySelector(`[data-collection-footer="${this.currentIndex}"]`);
        const newWrapper = this.container.querySelector(`[data-collection-panel="${newIndex}"]`);
        const newFooter = this.container.querySelector(`[data-collection-footer="${newIndex}"]`);

        if (!newWrapper) return;

        // Update tabs - find the clicked tab by its data attribute
        this.tabs.forEach(tab => {
            const tabIndex = parseInt(tab.dataset.collectionTab);
            if (tabIndex === newIndex) {
                tab.classList.add('collection-multi-slider__tab--active');
            } else {
                tab.classList.remove('collection-multi-slider__tab--active');
            }
        });

        // Hide current wrapper and footer
        if (currentWrapper) {
            currentWrapper.classList.remove('collection-multi-slider__wrapper--active');
            currentWrapper.style.display = 'none';
        }
        if (currentFooter) {
            currentFooter.classList.remove('collection-multi-slider__footer--active');
            currentFooter.style.display = 'none';
        }

        // Pause the previous slider's loop
        if (this.loops[this.currentIndex]) {
            this.loops[this.currentIndex].pause();
        }

        // Show new wrapper and footer (no animation, just instant switch)
        newWrapper.style.display = 'block';
        newWrapper.classList.add('collection-multi-slider__wrapper--active');

        if (newFooter) {
            newFooter.style.display = 'flex';
            newFooter.classList.add('collection-multi-slider__footer--active');
        }

        // Resume the horizontalLoop animation for the new slider
        if (this.loops[newIndex]) {
            this.loops[newIndex].pause(0);
        }

        const boxes = gsap.utils.toArray(newWrapper.querySelectorAll('.collection-multi-slider__item'));
        gsap.fromTo(boxes, { opacity: 0, yPercent: 40, }, { opacity: 1, yPercent: 0, duration: 1.6, stagger: { each: 0.1 }, ease: 'expo.out', });
        gsap.fromTo(newFooter, { opacity: 0, yPercent: 40, }, { opacity: 1, yPercent: 0, duration: 1.6, delay: 1, ease: 'expo.out', });


        this.currentIndex = newIndex;
    }


}
